import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  TemplateMediaEagerAllContext,
  TemplateMediaIndexContext,
  TemplateMediaRevealContext,
  TemplatePlainMediaContext,
  scheduleTemplateMediaRevealTick,
  type TemplateMediaRevealEntry,
  useTemplateMediaIndex,
  useTemplateMediaReveal,
  useTemplateMediaRevealContext,
} from "./useTemplateMediaReveal";

/**
 * Parallel image loads for website-template feeds.
 * First-screen / in-view tiles fetch together; each fades up as soon as it loads.
 */
export function TemplateFeedMediaRevealRoot({
  children,
  /** Search: load all tiles immediately (avoids blank→paint flicker on scroll). */
  eagerAll = false,
  /** Search: skip reveal queue — paint images immediately (no post-decode fade). */
  plainMedia = false,
}: {
  children: React.ReactNode;
  eagerAll?: boolean;
  plainMedia?: boolean;
}) {
  const mapRef = useRef(new Map<string, TemplateMediaRevealEntry>());
  const rafRef = useRef(0);
  const revealTimerRef = useRef(0);
  const maxWaitTimerRef = useRef(0);
  const eagerAllRef = useRef(eagerAll);
  eagerAllRef.current = eagerAll;

  const tick = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      scheduleTemplateMediaRevealTick(
        mapRef,
        revealTimerRef,
        maxWaitTimerRef,
        eagerAllRef.current,
      );
    });
  }, []);

  const register = useCallback(
    (
      entry: Pick<
        TemplateMediaRevealEntry,
        "id" | "index" | "requestLoad" | "reveal"
      > & { inView?: boolean },
    ) => {
      const existing = mapRef.current.get(entry.id);
      // Re-register (e.g. grid index shift) must NOT reset loaded/revealed —
      // that re-triggers reveal and looked like post-paint flicker.
      if (existing) {
        existing.index = entry.index;
        existing.inView = Boolean(entry.inView) || existing.inView;
        existing.requestLoad = entry.requestLoad;
        existing.reveal = entry.reveal;
        tick();
        return;
      }
      mapRef.current.set(entry.id, {
        id: entry.id,
        index: entry.index,
        inView: Boolean(entry.inView),
        started: false,
        loaded: false,
        revealed: false,
        requestLoad: entry.requestLoad,
        reveal: entry.reveal,
      });
      tick();
    },
    [tick],
  );

  const setInView = useCallback(
    (id: string, inView: boolean) => {
      const cur = mapRef.current.get(id);
      if (!cur || cur.inView === inView) return;
      cur.inView = inView;
      tick();
    },
    [tick],
  );

  const markLoaded = useCallback(
    (id: string) => {
      const cur = mapRef.current.get(id);
      if (!cur || cur.loaded) return;
      cur.loaded = true;
      if (!cur.revealed) {
        cur.revealed = true;
        cur.reveal();
      }
      tick();
    },
    [tick],
  );

  const unregister = useCallback(
    (id: string) => {
      mapRef.current.delete(id);
      tick();
    },
    [tick],
  );

  useEffect(
    () => () => {
      cancelAnimationFrame(rafRef.current);
      window.clearTimeout(revealTimerRef.current);
      window.clearTimeout(maxWaitTimerRef.current);
    },
    [],
  );

  const value = useMemo(
    () => ({
      register,
      setInView,
      markLoaded,
      unregister,
    }),
    [register, setInView, markLoaded, unregister],
  );

  return (
    <TemplatePlainMediaContext.Provider value={plainMedia}>
      <TemplateMediaEagerAllContext.Provider value={eagerAll}>
        <TemplateMediaRevealContext.Provider value={value}>
          {children}
        </TemplateMediaRevealContext.Provider>
      </TemplateMediaEagerAllContext.Provider>
    </TemplatePlainMediaContext.Provider>
  );
}

/** Sets the load-order index for descendant media (search FeedPost tiles). */
export function TemplateMediaIndexProvider({
  index,
  children,
}: {
  index: number;
  children: React.ReactNode;
}) {
  return (
    <TemplateMediaIndexContext.Provider value={index}>
      {children}
    </TemplateMediaIndexContext.Provider>
  );
}

export {
  useTemplateMediaReveal,
  useTemplateMediaRevealContext,
  useTemplateMediaIndex,
  TemplatePlainMediaContext,
};
